import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '@environments/environment';

export interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  route: string;
}

export interface OnboardingProgress {
  total_steps: number;
  completed_steps: number;
  percent_complete: number;
  steps: OnboardingStep[];
}

interface ApiOnboardingStep {
  key: string;
  title: string;
  description?: string;
  completed: boolean;
  link: string;
}

interface ApiOnboardingProgress {
  steps: ApiOnboardingStep[];
  completed_count: number;
  total_count: number;
  percent_complete: number;
}

const STEP_DESCRIPTIONS: Record<string, string> = {
  parse: 'Try parsing in the playground',
  template: 'Enable a public template or create your own',
  api_key: 'Create credentials for API access',
  organization: 'Configure your organization name and country',
  profile: 'Optionally add phone, job title, and department'
};

const DEFAULT_STEPS: OnboardingStep[] = [
  { id: 'parse', title: 'Run a test parse', description: STEP_DESCRIPTIONS['parse'], completed: false, route: '/playground' },
  { id: 'template', title: 'Use or customize a template', description: STEP_DESCRIPTIONS['template'], completed: false, route: '/templates' },
  { id: 'api_key', title: 'Create an API key', description: STEP_DESCRIPTIONS['api_key'], completed: false, route: '/api-keys' },
  { id: 'organization', title: 'Complete organization', description: STEP_DESCRIPTIONS['organization'], completed: false, route: '/settings/organization' },
  { id: 'profile', title: 'Complete profile extras', description: STEP_DESCRIPTIONS['profile'], completed: false, route: '/settings' }
];

@Injectable({
  providedIn: 'root'
})
export class OnboardingProgressService {
  private http = inject(HttpClient);
  private readonly refreshSubject = new Subject<void>();

  /** Emits when progress should be reloaded (e.g. after first parse). */
  readonly onRefresh$ = this.refreshSubject.asObservable();

  getProgress(): Observable<OnboardingProgress> {
    return this.http.get<ApiOnboardingProgress>(`${environment.apiUrl}/users/me/onboarding-progress`).pipe(
      map(raw => this.mapApiResponse(raw)),
      catchError(() => of(this.getDefaultProgress()))
    );
  }

  /** Re-fetch progress and notify subscribers (checklist, etc.). */
  refresh(): Observable<OnboardingProgress> {
    return this.getProgress().pipe(
      tap(() => this.refreshSubject.next())
    );
  }

  private mapApiResponse(raw: ApiOnboardingProgress): OnboardingProgress {
    return {
      total_steps: raw.total_count,
      completed_steps: raw.completed_count,
      percent_complete: raw.percent_complete,
      steps: raw.steps.map(step => ({
        id: step.key,
        title: step.title,
        description: step.description || STEP_DESCRIPTIONS[step.key] || '',
        completed: step.completed,
        route: step.link
      }))
    };
  }

  private getDefaultProgress(): OnboardingProgress {
    return {
      total_steps: DEFAULT_STEPS.length,
      completed_steps: 0,
      percent_complete: 0,
      steps: DEFAULT_STEPS
    };
  }
}
